<template>
    <div
        class="ai-robot"
        :class="{ 'ai-robot--interactive': interactive }"
        :style="boxStyle"
        :role="interactive ? 'button' : undefined"
        :title="interactive ? t('vue.ai_chat_robot_poke') : undefined"
        @click="onClick"
    >
        <!-- The bubble lives inside the bobbing body so it drifts together with
             the robot — it stays attached to its "mouth". -->
        <div class="ai-robot-body" :style="boxStyle">
            <transition name="greet">
                <div
                    v-if="interactive && showBubble && visible"
                    class="ai-robot-bubble"
                >
                    {{ phrase }}
                </div>
            </transition>

            <div class="ai-robot-poke" :class="{ 'is-poked': poked }">
                <AnimatedRobot />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import {
    useRobotChatter,
    useRobotPhrases,
} from "@/composables/useRobotChatter";

const props = withDefaults(
    defineProps<{
        size: number;
        interactive?: boolean;
        showBubble?: boolean;
        firstDelay?: number;
        gapMs?: number;
    }>(),
    {
        interactive: true,
        showBubble: true,
        firstDelay: 600,
        gapMs: 4200,
    },
);

const { t } = useTranslations();

const boxStyle = computed(() => ({
    width: `${props.size}px`,
    height: `${props.size}px`,
}));

const robotPhrases = useRobotPhrases();
const { visible, phrase, poke } = useRobotChatter(() => robotPhrases.value, {
    firstDelay: props.firstDelay,
    gapMs: props.gapMs,
});

// Clicking the mascot swaps its phrase and triggers a one-shot bounce. Toggling
// the class off then on across a tick restarts the CSS animation each click.
const poked = ref(false);
const onClick = async (): Promise<void> => {
    if (props.interactive === false) {
        return;
    }
    poke();
    poked.value = false;
    await nextTick();
    poked.value = true;
};
</script>

<!-- Keyframes are GLOBAL (not scoped) with unique names so Vue's scoped-style
     rewriting can never rename or drop them. Deliberately NOT gated on
     prefers-reduced-motion (see memory: the user runs reduce-motion). -->
<style>
/* Friendly-mascot idle loop (Lottie-inspired): a smooth levitation with a
   gentle tilt that leans into the rise/fall, plus a barely-there breathing
   scale. All one synced, organic motion. */
@keyframes aiRobotHover {
    0% {
        transform: translateY(0) rotate(0deg) scale(1);
    }
    20% {
        transform: translateY(-3.5%) rotate(1.2deg) scale(1.008);
    }
    50% {
        transform: translateY(-7%) rotate(0deg) scale(1.016);
    }
    80% {
        transform: translateY(-3.5%) rotate(-1.2deg) scale(1.008);
    }
    100% {
        transform: translateY(0) rotate(0deg) scale(1);
    }
}

@keyframes aiRobotPoke {
    0% {
        transform: translateY(0) scale(1) rotate(0deg);
    }
    30% {
        transform: translateY(-13%) scale(1.05) rotate(-6deg);
    }
    60% {
        transform: translateY(2%) scale(0.98) rotate(4deg);
    }
    100% {
        transform: translateY(0) scale(1) rotate(0deg);
    }
}
</style>

<style scoped>
.ai-robot {
    position: relative;
    display: inline-block;
}

.ai-robot--interactive {
    cursor: pointer;
}

/* A single, gentle levitation — decent and consistent. The explicit pixel size
   gives the percentage keyframe a height to resolve against. */
.ai-robot-body {
    position: relative;
    animation: aiRobotHover 4s ease-in-out infinite;
    will-change: transform;
}

.ai-robot-poke {
    width: 100%;
    height: 100%;
}

.ai-robot-poke.is-poked {
    animation: aiRobotPoke 0.6s ease;
}

.ai-robot :deep(.robot-anim),
.ai-robot :deep(.robot-anim svg) {
    width: 100% !important;
    height: 100% !important;
}

/* Our own bubble replaces the shared robot's built-in "Bye" message. */
.ai-robot :deep(#robotMessage) {
    display: none !important;
}

.ai-robot-bubble {
    position: absolute;
    bottom: 100%;
    left: 50%;
    z-index: 10;
    margin-bottom: 10px;
    transform: translateX(-50%);
    white-space: nowrap;
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    background: #fff;
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #374151;
    box-shadow:
        0 10px 15px -3px rgb(0 0 0 / 0.1),
        0 4px 6px -4px rgb(0 0 0 / 0.1);
}

/* Little tail pointing down toward the robot's mouth. */
.ai-robot-bubble::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    width: 9px;
    height: 9px;
    margin-top: -5px;
    transform: translateX(-50%) rotate(45deg);
    background: #fff;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
}

.greet-enter-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.greet-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}
.greet-enter-from,
.greet-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(6px) scale(0.9);
}
</style>
